import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpineAnimationMultiTrack} from '@fantasmagames/core/src/display/SpineAnimationMultiTrack';
import {SpineButton} from '@fantasmagames/core/src/display/SpineButton';
import {ResourcesLoader} from '@fantasmagames/core/src/loader/ResourcesLoader';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {UpdateManager} from '@fantasmagames/core/src/update/UpdateManager';
import {ViewTypes} from '@fantasmagames/slot/src/common/view/View';
import {Dimensions, Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import Skeleton = PIXI.spine.core.Skeleton;
import {LocalizedSpineButton} from '@fantasmagames/core/src/display/LocalizedSpineButton';

export class SpineUtils {
	protected _resources: ResourcesLoader;
	private _defaultUpdateManager: UpdateManager;
	protected _defaultContainer: PIXI.Container;

	protected _currentSpineName: string;
	protected _currentAnimationName: string;
	protected _currentLoopState: boolean;
	protected _currentPosition: Position;
	protected _currentRotation: number;
	protected _currentDontDestroyOnCompleteState: boolean;
	protected _currentContainer: PIXI.Container;
	protected _currentUpdateManager: UpdateManager;
	protected _currentOnComplete: Function;
	protected _chainedIdleAnimation: string;
	protected _currentViewType: ViewTypes;

	protected _isCreationStarted: boolean = false;
	protected _currentLoopLimit: number;

	constructor(resources: ResourcesLoader, updateManager: UpdateManager, container: PIXI.Sprite) {
		this._resources = resources;
		this._defaultUpdateManager = updateManager;
		this._defaultContainer = container;
	}

	public setSkin(skeleton: Skeleton, skinName: string): void {
		skeleton.setSkin(null);
		skeleton.setSkinByName(skinName);
	}

	public startSpineAnimationCreation(spineName: string, animationName: string, loop: boolean, loopLimit: number = -1): SpineUtils {
		if (this._isCreationStarted) {
			throw new Error('SpineUtils last object creation was not finished properly. Maybe createAndStart was not called');
		}
		this._isCreationStarted = true;
		this._currentSpineName = spineName;
		this._currentAnimationName = animationName;
		this._currentLoopState = loop;
		this._currentLoopLimit = loopLimit;

		// setting up default values
		this._currentPosition = {x: 0, y: 0};
		this._currentRotation = 0;
		this._currentDontDestroyOnCompleteState = false;
		this._currentContainer = this._defaultContainer;
		this._currentUpdateManager = this._defaultUpdateManager;
		this._currentOnComplete = null;
		this._chainedIdleAnimation = null;
		this._currentViewType = ViewTypes.DESKTOP;

		return this;
	}

	public viewType(viewType: ViewTypes): SpineUtils {
		this._currentViewType = viewType;

		return this;
	}

	public onComplete(callback: Function): SpineUtils {
		this._currentOnComplete = callback;

		return this;
	}

	public position(newX: number | Position, newY?: number): SpineUtils {
		if ((newX as any).x) {
			this._currentPosition = newX as Position;
		} else {
			this._currentPosition = {x: newX as number, y: newY};
		}

		return this;
	}

	public rotation(angle: number): SpineUtils {
		this._currentRotation = angle;

		return this;
	}

	public dontDestroyOnCompleted(newDontDestroyOnCompletedStatus: boolean): SpineUtils {
		this._currentDontDestroyOnCompleteState = newDontDestroyOnCompletedStatus;

		return this;
	}

	public container(newContainer: PIXI.Container): SpineUtils {
		this._currentContainer = newContainer;

		return this;
	}

	public updateManager(newUpdateManager: UpdateManager): SpineUtils {
		this._currentUpdateManager = newUpdateManager;

		return this;
	}

	public chainIdleAnimation(animation: string): SpineUtils {
		this._chainedIdleAnimation = animation;

		return this;
	}

	public createAndStart(): SpineAnimation {
		const spineAnimation: SpineAnimation = new SpineAnimation(
			new Spine(this._resources.getSpine(this._currentSpineName, this._currentViewType)),
			this._currentAnimationName,
			this._currentLoopState,
			this._currentDontDestroyOnCompleteState,
		);

		if (this._currentLoopLimit > 0) {
			spineAnimation.setLoopLimit(this._currentLoopLimit);
		}

		if (this._chainedIdleAnimation) {
			spineAnimation.chainIdleAnimation(this._chainedIdleAnimation);
		}

		if (this._currentRotation) {
			spineAnimation.container.rotation = this._currentRotation;
		}

		spineAnimation.start();

		if (!__PRODUCTION__) {
			if (!(spineAnimation.container as Spine).skeleton) {
				throw new Error(
					`Spine Skeleton does not exist. Probably missing keyframes in starting animation. Skeleton-name:${spineAnimation.container.name} Animation-name:${spineAnimation.name}`,
				);
			}
		}

		this._currentUpdateManager.add(spineAnimation);
		this._currentContainer.addChild(spineAnimation.container);

		spineAnimation.container.position.set(this._currentPosition.x, this._currentPosition.y);

		if (this._currentOnComplete) {
			spineAnimation.setOnComplete(this._currentOnComplete);
		}

		this._isCreationStarted = false;

		return spineAnimation;
	}

	public startExit(animation: SpineAnimation, exitAnimationName: string, onComplete: Function): void {
		if (!animation) {
			throw new Error('SpineUtils::startExit animation can not be null');
		}

		animation.unChainIdleAnimation();
		animation.changeAnimationTo(exitAnimationName, false);
		animation.setOnComplete(onComplete);
	}

	public createMultitrack(): SpineAnimationMultiTrack {
		const spineMultitrack: SpineAnimationMultiTrack = new SpineAnimationMultiTrack(
			new Spine(this._resources.getSpine(this._currentSpineName)),
			this._currentAnimationName,
			this._currentLoopState,
		);

		if (this._chainedIdleAnimation) {
			spineMultitrack.chainIdleAnimation(this._chainedIdleAnimation);
		}

		spineMultitrack.start();

		this._currentUpdateManager.add(spineMultitrack);
		this._currentContainer.addChild(spineMultitrack.container);

		spineMultitrack.container.position.set(this._currentPosition.x, this._currentPosition.y);

		if (this._currentOnComplete) {
			spineMultitrack.setOnComplete(this._currentOnComplete);
		}

		this._isCreationStarted = false;

		return spineMultitrack;
	}

	public replaceElementInSpineAnimation(animation: SpineAnimation, slotName: string, newSprite: PIXI.Sprite | PIXI.Text): void {
		const slotIndex = (animation.container as Spine).skeleton.findSlotIndex(slotName);
		const container = (animation.container as Spine).slotContainers[slotIndex];

		this.replaceItemInContainer(container, newSprite);
	}

	public replaceElementInSpineMultiTrack(
		animation: SpineAnimationMultiTrack,
		slotName: string,
		newSprite: PIXI.Sprite | PIXI.Text,
	): void {
		const slotIndex = (animation.container as Spine).skeleton.findSlotIndex(slotName);
		const container = (animation.container as Spine).slotContainers[slotIndex];

		this.replaceItemInContainer(container, newSprite);
	}

	public replaceElementInSpine(spine: Spine, slotName: string, newSprite: PIXI.Sprite): void {
		const container: PIXI.Container = spine.slotContainers[spine.skeleton.findSlotIndex(slotName)];

		if (!__PRODUCTION__) {
			if (!container) {
				throw new Error(`SpineUtils::replaceElementInSpine - no Slot named: ${slotName} in ${spine.spineData.name}`);
			}
		}

		this.replaceItemInContainer(container, newSprite);
	}

	public destroyAllPlaceholderGraphic(spine: Spine): void {
		this.destroyAllGraphicsWithName(spine, 'PLACEHOLDER');
	}

	public destroyAllGraphicsWithName(spine: Spine, textureName: string): void {
		const slotContainers = spine.slotContainers;
		if (slotContainers && slotContainers.length) {
			slotContainers.forEach((slot) => {
				if (slot.children) {
					slot.children.forEach((child) => {
						if ((child as any).region && (child as any).region.name === textureName) {
							child.destroy();
						}
					});
				}
			});
		}
	}

	public findSlotContainerByName(spine: Spine, slotName: string): PIXI.Container {
		return spine.slotContainers[spine.skeleton.findSlotIndex(slotName)];
	}

	public clearSlotContainerByName(spine: Spine, slotName: string): void {
		this.findSlotContainerByName(spine, slotName).children.forEach((child) => {
			child.visible = false;
			child.parent = null;
		});
	}

	public stopAndDestroyAnimation(animation: SpineAnimation | SpineAnimationMultiTrack): void {
		if (animation) {
			animation.forceDestroy();
			if (animation.container.parent) {
				animation.container.parent.removeChild(animation.container);
			}
		}
	}

	public createSpineButton(
		resourceName: string,
		clickCallback: Function,
		loopIdle: boolean = false,
		loopDown?: boolean,
		loopOver?: boolean,
		loopDisabled?: boolean,
	): SpineButton {
		const button: SpineButton = new SpineButton(clickCallback);
		button.setupButton(new Spine(this._resources.getSpine(resourceName)), this._defaultUpdateManager);
		button.setAnimations('IDLE', 'CLICK', 'HOVER', 'DISABLED');
		button.setAnimationsLoop(loopIdle, loopDown, loopOver, loopDisabled);

		button.anchor.set(0.5, 0.5);

		button.enable();

		return button;
	}

	public createLocalizedSpineButton(
		resourceName: string,
		clickCallback: Function,
		locals: string[],
		slots: string[],
		loopIdle: boolean = false,
		loopDown?: boolean,
		loopOver?: boolean,
		loopDisabled?: boolean,
	): LocalizedSpineButton {
		const button: LocalizedSpineButton = new LocalizedSpineButton(clickCallback, this, this._resources);
		button.setupButton(new Spine(this._resources.getSpine(resourceName)), this._defaultUpdateManager);
		button.setAnimations('IDLE', 'CLICK', 'HOVER', 'DISABLED');
		button.setAnimationsLoop(loopIdle, loopDown, loopOver, loopDisabled);

		button.setupLocales(
			locals[0],
			locals[1] ? locals[1] : locals[0],
			locals[2] ? locals[2] : locals[0],
			locals[3] ? locals[3] : locals[0],
		);
		button.setupButtonSlots(slots[0], slots[1] ? slots[1] : slots[0], slots[2] ? slots[2] : slots[0], slots[3] ? slots[3] : slots[0]);

		button.anchor.set(0.5, 0.5);

		button.enable();

		return button;
	}

	public getAnimationDuration(resourceName: string, animationName: string): number {
		return this._resources.getSpine(resourceName).findAnimation(animationName).duration;
	}

	public getAnimationsTotalDuration(resourceName: string, animationNames: string[]): number {
		let totalDuration: number = 0;

		for (const anim in animationNames) {
			if (animationNames.hasOwnProperty(anim)) {
				const name: string = animationNames[anim];
				totalDuration += this.getAnimationDuration(resourceName, name);
			}
		}

		return totalDuration;
	}

	public getSkeletonDataFromSpine(spine: PIXI.spine.Spine): PIXI.spine.core.SkeletonData {
		return spine.skeleton.data;
	}

	public getSkeletonDataFromSpineAnimation(spineAnimation: SpineAnimation): PIXI.spine.core.SkeletonData {
		return (spineAnimation.container as PIXI.spine.Spine).skeleton.data;
	}

	public getSkeletonDataFromSpineAnimationMultiTrack(spineAnimation: SpineAnimationMultiTrack): PIXI.spine.core.SkeletonData {
		return (spineAnimation.container as PIXI.spine.Spine).skeleton.data;
	}

	/**
	 *  Takes a skeleton data object and parses the absolute position of the bone in setup pose.
	 * @param {PIXI.spine.core.SkeletonData} skeletonData
	 * @param {string} boneName
	 * @returns {Position}
	 * @memberof SpineUtils
	 */
	public getBoneAbsolutePosition(skeletonData: PIXI.spine.core.SkeletonData, boneName: string): Position {
		let bone = skeletonData.findBone(boneName);
		const position = {x: 0, y: 0};

		while (bone.parent) {
			position.x += bone.x;
			position.y += bone.y;

			bone = bone.parent;
		}

		return position;
	}

	public getSlotAttachmentData(skeletonData: PIXI.spine.core.SkeletonData, slot: string): PIXI.spine.core.Attachment {
		const slotObject = skeletonData.findSlot(slot);

		return skeletonData.defaultSkin.getAttachment(slotObject.index, slotObject.attachmentName);
	}

	public getSlotAttachmentDimensions(skeletonData: PIXI.spine.core.SkeletonData, slot: string): Dimensions {
		const attachment: PIXI.spine.core.RegionAttachment = this.getSlotAttachmentData(
			skeletonData,
			slot,
		) as PIXI.spine.core.RegionAttachment;

		return {width: attachment.width, height: attachment.height, scale: {x: attachment.scaleX, y: attachment.scaleY}};
	}

	protected replaceItemInContainer(container: PIXI.Container, sprite: PIXI.Sprite | PIXI.Text): void {
		sprite.anchor.set(0.5, 0.5);
		sprite.position.set(-sprite.height * 0.5);
		if (container.children[0]) {
			sprite.transform = container.children[0].transform;
		} else {
			sprite.rotation = Math.PI;
			sprite.skew.y = Math.PI;
		}
		container.removeChildren();
		container.addChild(sprite);
	}

	public get defaultUpdateManager(): UpdateManager {
		return this._defaultUpdateManager;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/spine/SpineUtils.ts